import { createHash } from "node:crypto";
function stableBucket(value) {
    const digest = createHash("sha256").update(value).digest("hex").slice(0, 8);
    const int = parseInt(digest, 16);
    return int % 100; // 0..99
}
function matchesRule(rule, input) {
    const c = rule.conditions ?? {};
    if (c.envs?.length && !c.envs.includes(input.env))
        return false;
    if (c.restaurantIds?.length) {
        if (!input.restaurantId)
            return false;
        if (!c.restaurantIds.includes(input.restaurantId))
            return false;
    }
    if (c.userIds?.length) {
        if (!input.userId)
            return false;
        if (!c.userIds.includes(input.userId))
            return false;
    }
    if (c.membershipRoles?.length) {
        if (!input.membershipRole)
            return false;
        if (!c.membershipRoles.includes(input.membershipRole))
            return false;
    }
    if (rule.rolloutPercent >= 100)
        return true;
    if (rule.rolloutPercent <= 0)
        return false;
    const rolloutId = input.userId ?? input.restaurantId ?? `${input.env}::anonymous`;
    return stableBucket(`${rule.flagKey}::${rolloutId}`) < rule.rolloutPercent;
}
/**
 * Evaluate feature flags for a single viewer context.
 *
 * Precedence:
 * 1) Explicit override (user or restaurant) if present
 * 2) First matching enabled rule by priority
 * 3) Base flag enabled boolean
 *
 * This returns only booleans (no PII, no rule details), safe to send to clients.
 */
export function evaluateFeatureFlags(params) {
    const { flags, rules, overrides, input } = params;
    const rulesByFlag = new Map();
    for (const r of rules) {
        const list = rulesByFlag.get(r.flagKey) ?? [];
        list.push(r);
        rulesByFlag.set(r.flagKey, list);
    }
    for (const [k, list] of rulesByFlag.entries()) {
        list.sort((a, b) => a.priority - b.priority);
        rulesByFlag.set(k, list);
    }
    const overridesByFlag = new Map();
    for (const o of overrides) {
        const list = overridesByFlag.get(o.flagKey) ?? [];
        list.push(o);
        overridesByFlag.set(o.flagKey, list);
    }
    const result = {};
    for (const flag of flags) {
        const flagKey = flag.key;
        const olist = overridesByFlag.get(flagKey) ?? [];
        const override = olist.find((o) => {
            if (o.userId && input.userId && o.userId === input.userId)
                return true;
            if (o.restaurantId &&
                input.restaurantId &&
                o.restaurantId === input.restaurantId)
                return true;
            return false;
        });
        if (override) {
            result[flagKey] = override.enabled;
            continue;
        }
        const rlist = rulesByFlag.get(flagKey) ?? [];
        const matchingRule = rlist.find((r) => r.enabled && matchesRule(r, input));
        if (matchingRule) {
            result[flagKey] = matchingRule.enabled;
            continue;
        }
        result[flagKey] = flag.enabled;
    }
    return result;
}
